import classNames from 'classnames/bind';
import styles from './indicator.module.scss';

const cx = classNames.bind(styles);

interface PromotionCarouselIndicatorProps {
  index: number;
  total: number;
  isDraging: boolean;
  ratio: number;
  onMoveTo: (index: number) => void;
}

export const PromotionCarouselIndicator = ({
  index,
  total,
  isDraging,
  ratio,
  onMoveTo,
}: PromotionCarouselIndicatorProps) => {
  const indicatorWidth = 6;
  const indicatorMargin = 4;
  const indicatorGroupWidth = indicatorWidth + indicatorMargin;
  const isFirstIndex = index === 0;
  const isLastIndex = index === total - 1;

  /**
   * Get the offset of start of the maker
   */
  const getMakerOffset = () => {
    if (!isDraging) return index * indicatorGroupWidth;

    const numberOfItemsCrossed = getNumberOfItemsCrossed(ratio, index, total);

    // User is dragging to the left
    if (ratio < 0) {
      // If it's the first item and the user is dragging to the left, do not move the start of the maker
      if (isFirstIndex) return index * indicatorGroupWidth;
      return (index - numberOfItemsCrossed) * indicatorGroupWidth;
    }

    // If user is dragging to the right, do not move the start of the maker
    return index * indicatorGroupWidth;
  };

  /**
   * Ger the width of the maker
   */
  const getMakerWidth = () => {
    if (!isDraging) return indicatorWidth;

    // If it's the first item and the user is dragging to the left, do not stretch the marker
    if (isFirstIndex && ratio <= 0) return indicatorWidth;

    // If it's the last item and the user is dragging to the right, do not stretch the marker
    if (isLastIndex && ratio >= 0) return indicatorWidth;

    const numberOfItemsCrossed = getNumberOfItemsCrossed(ratio, index, total);
    const totalWidth =
      indicatorWidth + indicatorGroupWidth * numberOfItemsCrossed;
    return totalWidth;
  };

  const indicatorGroup = cx({
    'indicator-group': true,
  });

  const indicatorList = Array.from({ length: total }, (_, i) => {
    const indicatorClass = cx({
      indicator: true,
      active: i === index,
    });
    return (
      <li
        className={indicatorClass}
        style={{
          cursor: 'pointer',
        }}
        key={i}
        onClick={() => onMoveTo(i)}
      />
    );
  });

  const width = indicatorWidth * total + indicatorMargin * (total - 1);

  return (
    <ul
      className={indicatorGroup}
      style={{
        width,
      }}
    >
      <div
        style={{
          transition: 'all 0.2s ease',
          width: `${getMakerWidth()}px`,
          left: `${getMakerOffset()}px`,
          height: '6px',
          borderRadius: '3px',
          background: 'black',
          position: 'absolute',
          top: 0,
        }}
      />

      {indicatorList}
    </ul>
  );
};

function getNumberOfItemsCrossed(
  ratio: number,
  currentIndex: number,
  total: number,
) {
  // User is dragging to the right
  if (ratio > 0) {
    const isPassingToNextItem = ratio % 1 > 0.5;
    const numberOfItemsCrossed = isPassingToNextItem
      ? Math.ceil(ratio)
      : Math.floor(ratio);

    // Check if the user is dragging to the last item, if so, do not stretch the maker anymore
    return Math.min(total - 1 - currentIndex, numberOfItemsCrossed);
  }

  const isPassingToNextItem = ratio % 1 < -0.5;
  const numberOfItemsCrossed = Math.abs(
    isPassingToNextItem ? Math.floor(ratio) : Math.ceil(ratio),
  );

  // Check if the user is dragging to the first item, if so, do not stretch the maker anymore
  return Math.min(currentIndex, numberOfItemsCrossed);
}
